import type { TeamInstallationStatusGetResponse } from "@bezoned/shared/types/api/team/team-installation-status-get.types.js";

import { FullScreenLoader } from "../../../components/ui/loaders/full-screen-loader.js";
import { TeamInstallationAlreadyInstalled } from "./team-installation-already-installed.js";
import { TeamInstallationFailure } from "./team-installation-failure.js";
import { TeamInstallationRequired } from "./team-installation-required.js";
import { TeamInstallationSuccess } from "./team-installation-success.js";

interface TeamInstallationContentProps {
  loading: boolean;
  msTeamId: string | null;
  redirect: {
    url: string;
    openInNewTab: boolean;
  };
  installationState: TeamInstallationStatusGetResponse["state"] | undefined;
  error: Error | null;
}

export const TeamInstallationContent = ({
  loading,
  msTeamId,
  redirect,
  installationState,
  error,
}: TeamInstallationContentProps) => {
  if (loading) {
    return <FullScreenLoader message="Checking team..." />;
  }

  // No team ID means we are outside of Teams platform
  if (!msTeamId && !loading && !error) {
    return (
      <FullScreenLoader message="Opening lobby..." redirectUrl={redirect.url} />
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen w-screen items-center justify-center bg-[#f6f5f3] p-6">
        <TeamInstallationFailure redirect={redirect} />
      </div>
    );
  }

  // Tenant does not exist
  if (installationState === "new_tenant") {
    return (
      <div className="flex min-h-screen w-screen items-center justify-center bg-[#f6f5f3] p-6">
        <TeamInstallationRequired redirect={redirect} />
      </div>
    );
  }

  // Regular (non-enterprise) tenants are redirected to Lobby for now to allow MS to verify
  // the functionality of the app.
  // Later on, we should return the commented out code instead
  if (installationState === "regular") {
    // return (
    //   <div className="flex min-h-screen w-screen items-center justify-center bg-[#f6f5f3] p-6">
    //     <TeamInstallationEnterpriseOnly redirect={redirect} />
    //   </div>
    // )
    return (
      <FullScreenLoader message="Opening lobby..." redirectUrl={redirect.url} />
    );
  }

  // Tenant with team already added through RSC
  if (installationState === "enterprise_installed") {
    return (
      <div className="flex min-h-screen w-screen items-center justify-center bg-[#f6f5f3] p-6">
        <TeamInstallationAlreadyInstalled redirect={redirect} />
      </div>
    );
  }

  if (installationState === "enterprise") {
    return (
      <div className="flex min-h-screen w-screen items-center justify-center bg-[#f6f5f3] p-6">
        <TeamInstallationSuccess redirect={redirect} msTeamId={msTeamId} />
      </div>
    );
  }

  // Just to be sure
  return <FullScreenLoader message="Checking team..." />;
};
